import React from "react";
import { Tabs } from "antd";
import { map } from "lodash";
import { EComModal } from "components/EComModal";
import { EMPTY } from "constants/common";
import PromptEditor from "../../PromptEditor";
import { PromptVariableRules } from "../../PromptEditor/variables";
import { PROMPT_DIFF_KIND, PromptSettingChange, PromptTextDiff } from "../promptDiff";
import {
  DIFF_ADDED_LINES,
  DIFF_LIVE_NOTE,
  DIFF_REMOVED_LINES,
  DIFF_TRUNCATED,
  REFERENCE_TAB_CHANGES,
  REFERENCE_TAB_TEXT,
} from "../configs";
import styles from "./Reference.module.scss";

interface Props {
  visible: boolean;
  referenceTitle: string;
  referenceText: string;
  rules: PromptVariableRules;
  diff?: PromptTextDiff;
  settingChanges?: PromptSettingChange[];
  onClose: () => void;
}

const SIGN: Record<string, string> = {
  [PROMPT_DIFF_KIND.added]: "+",
  [PROMPT_DIFF_KIND.removed]: "−",
  [PROMPT_DIFF_KIND.unchanged]: " ",
};

const LINE_CLASS: Record<string, string> = {
  [PROMPT_DIFF_KIND.added]: styles.diffLineAdded,
  [PROMPT_DIFF_KIND.removed]: styles.diffLineRemoved,
  [PROMPT_DIFF_KIND.unchanged]: styles.diffLine,
};

function CompareModal({ visible, referenceTitle, referenceText, rules, diff, settingChanges, onClose }: Props) {
  const hasChanges = !!settingChanges?.length || !!(diff && !diff.isIdentical);

  const text = referenceText ? (
    <PromptEditor value={referenceText} rules={rules} readOnly autoHeight />
  ) : (
    <div className={styles.referenceText}>{EMPTY}</div>
  );

  const changes = (
    <div className={styles.changesBody}>
      {!!settingChanges?.length && (
        <div className={styles.settingChanges}>
          {map(settingChanges, ({ label, from, to }) => (
            <div key={label} className={styles.settingChange}>
              {`${label}: `}
              <span className={styles.settingFrom}>{from}</span>
              {" → "}
              <span className={styles.settingTo}>{to}</span>
            </div>
          ))}
        </div>
      )}

      {!!diff && !diff.isIdentical && (
        <>
          <div className={styles.diffCounts}>
            <span className={styles.diffCountAdded}>{DIFF_ADDED_LINES(diff.added)}</span>
            <span className={styles.diffCountRemoved}>{DIFF_REMOVED_LINES(diff.removed)}</span>
          </div>
          {diff.isTruncated && <div className={styles.panelEmpty}>{DIFF_TRUNCATED}</div>}

          <div className={styles.diffBody}>
            {map(diff.lines, ({ kind, text: line }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${index}-${kind}`} className={LINE_CLASS[kind]}>
                <span className={styles.diffSign}>{SIGN[kind]}</span>
                <span className={styles.diffText}>{line || " "}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.diffLiveNote}>{DIFF_LIVE_NOTE}</div>
    </div>
  );

  return (
    <EComModal
      title={referenceTitle}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={760}
      bodyStyle={{ overflow: "hidden" }}
    >
      {hasChanges ? (
        <Tabs defaultActiveKey={REFERENCE_TAB_CHANGES}>
          <Tabs.TabPane tab={REFERENCE_TAB_CHANGES} key={REFERENCE_TAB_CHANGES}>
            <div className={styles.tabPane}>{changes}</div>
          </Tabs.TabPane>
          <Tabs.TabPane tab={REFERENCE_TAB_TEXT} key={REFERENCE_TAB_TEXT}>
            <div className={styles.tabPane}>{text}</div>
          </Tabs.TabPane>
        </Tabs>
      ) : (
        <div className={styles.textPane}>{text}</div>
      )}
    </EComModal>
  );
}

export default CompareModal;
